import { Typography, Button } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { targetActions } from '../../redux/slices/target.slice';
import { RootState } from '../../redux/store';
import Spinner from '../../components/spinner';
import { globalActions } from '../../redux/slices/global.slice';
import { EThingStatuses } from '../../types/@thing';

import { styles } from './target.styles';
import { ROUTES } from '../../helpers/constans';
import ButtonWithLoading from '../../components/button-with-loading';

const Target = (): ReactElement => {
  const { targetId } = useParams();
  const { isLoaded, info, isFinderMessageSending, isFinderMessageSent } = useSelector(
    (state: RootState) => state.target,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [message, setMessage] = useState('');
  const [finderName, setFinderName] = useState('');
  const [finderEmail, setFinderEmail] = useState('');
  const [finderPhone, setFinderPhone] = useState('');

  useEffect(() => {
    targetId && dispatch(targetActions.getTarget({ id: targetId }));
    dispatch(targetActions.resetFinderMessageState());
  }, [targetId, dispatch]);

  const handleBecomeOwner = () => {
    dispatch(globalActions.setPendingThingId(targetId));
    navigate(ROUTES.registration);
  };

  const handleSendFinderMessage = () => {
    if (!targetId || !message.trim()) return;

    dispatch(
      targetActions.sendFinderMessage({
        id: targetId,
        message: message.trim(),
        finderName: finderName.trim() || undefined,
        finderEmail: finderEmail.trim() || undefined,
        finderPhone: finderPhone.trim() || undefined,
      }),
    );
  };

  return (
    <Fragment>
      <Box sx={styles.container}>
        {!isLoaded && <Spinner />}

        {(isLoaded && info && (
          <Box sx={styles.mainBox}>
            <Typography sx={styles.mainTitle}>{info.name}</Typography>

            <Box sx={styles.imageBox}>
              <Box sx={styles.targetImage(info.images?.[0]?.path)} />
            </Box>

            <Box sx={styles.descriptionsBox}>
              <Typography sx={styles.title}>Status</Typography>
              <Typography sx={styles.status}>{info.status}</Typography>

              <Typography sx={styles.title}>Description</Typography>
              <Typography sx={styles.descriptions}>{info.description}</Typography>

              {info.scanView && (
                <Box sx={styles.scanViewBox}>
                  <Typography sx={styles.scanViewTitle}>{info.scanView.title}</Typography>
                  <Typography sx={styles.scanViewMessage}>{info.scanView.message}</Typography>
                </Box>
              )}

              {info.ownerInfo?.isPrivate && (
                <Fragment>
                  <Typography sx={styles.title}>Owner's name</Typography>
                  <Typography sx={styles.descriptions}>{info.ownerInfo.name}</Typography>
                </Fragment>
              )}

              {info.ownerInfo?.isPrivate === false && (
                <Fragment>
                  <Typography sx={styles.title}>Owner's name</Typography>
                  <Typography sx={styles.descriptions}>{info.ownerInfo.name}</Typography>

                  <Typography sx={styles.title}>Owner's phone number</Typography>
                  <Typography sx={styles.descriptions}>{info.ownerInfo?.phone}</Typography>

                  <Typography sx={styles.title}>Owner's twitter account</Typography>
                  <Typography sx={styles.descriptions}>{info.ownerInfo?.twitterId}</Typography>

                  <Typography sx={styles.title}>Owner's instagram account</Typography>
                  <Typography sx={styles.descriptions}>{info.ownerInfo?.instagramId}</Typography>
                </Fragment>
              )}

              {info.scanView?.allowFinderMessage && !isFinderMessageSent && (
                <Box sx={styles.finderFormBox}>
                  <Typography sx={styles.title}>Message to owner</Typography>
                  <textarea
                    style={styles.finderTextarea as React.CSSProperties}
                    placeholder="Describe where you found the item and how the owner can recover it"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />

                  <input
                    style={styles.finderInput as React.CSSProperties}
                    placeholder="Your name (optional)"
                    value={finderName}
                    onChange={(e) => setFinderName(e.target.value)}
                  />

                  <input
                    style={styles.finderInput as React.CSSProperties}
                    placeholder="Your email (optional)"
                    value={finderEmail}
                    onChange={(e) => setFinderEmail(e.target.value)}
                  />

                  <input
                    style={styles.finderInput as React.CSSProperties}
                    placeholder="Your phone (optional)"
                    value={finderPhone}
                    onChange={(e) => setFinderPhone(e.target.value)}
                  />

                  <Box sx={styles.finderFormActions}>
                    <ButtonWithLoading
                      styles={styles.finderSubmitButton}
                      isLoading={!!isFinderMessageSending}
                      disabled={!message.trim()}
                      handler={handleSendFinderMessage}
                      buttonName={info.scanView.messageButtonLabel || 'Send message to owner'}
                    />
                  </Box>
                </Box>
              )}

              {info.scanView?.allowFinderMessage && isFinderMessageSent && (
                <Box sx={styles.finderSuccess}>
                  Thank you. Your message has been sent to the owner.
                </Box>
              )}
            </Box>

            {info.templateId && info.status === EThingStatuses.unowned && (
              <Box sx={styles.buttonBecomeOwnerBox}>
                <Button sx={styles.buttonBecomeOwner} onClick={handleBecomeOwner}>
                  become the owner
                </Button>
              </Box>
            )}
          </Box>
        )) || <Box>Object not found</Box>}
      </Box>
    </Fragment>
  );
};

export default Target;